import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/BuyerUser';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const BUYER_USER_API_REDUCER_KEY = 'buyer-users';
export const BUYER_USER_PATH = 'buyer-users';

export const buyerUserApi = createApi({
    reducerPath: BUYER_USER_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['BuyerUser', 'BuyerUsers'],
    endpoints: (builder) => ({
        getBuyerUserList: builder.query<PaginationResult<Entity.BuyerUser>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ BUYER_USER_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['BuyerUsers'],
        }),
        getBuyerUser: builder.query<Entity.BuyerUser, Entity.PrimaryBuyerUser>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = buyerUserNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ BUYER_USER_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['BuyerUser'],
        }),
        getBuyerUserMultiple: builder.query<Entity.BuyerUser, Entity.PrimaryBuyerUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ BUYER_USER_PATH }/ids${Entity.PrimaryBuyerUser.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['BuyerUsers'],
        }),
        getBuyerUserPresentation: builder.query<PaginationResult<Entity.BuyerUser>, Entity.PresentationBuyerUserRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ BUYER_USER_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['BuyerUsers'],
        }),
        
        insertBuyerUser: builder.mutation<Entity.BuyerUser, Entity.CreateBuyerUser>({
            query: (toInsert) => {
                return ({
                    url: `/${ BUYER_USER_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    buyerUserApi.util.upsertQueryData('getBuyerUser', Entity.PrimaryBuyerUser.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['BuyerUsers'],
        }),
        insertMultipleBuyerUser: builder.mutation<Entity.BuyerUser[], Entity.CreateBuyerUser[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ BUYER_USER_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            buyerUserApi.util.upsertQueryData('getBuyerUser', Entity.PrimaryBuyerUser.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['BuyerUsers'],
        }),
        updateBuyerUser: builder.mutation<Entity.BuyerUser, Entity.UpdateEntityBuyerUser>({
            query: (toUpdate) => {
                return ({
                    url: `/${ BUYER_USER_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['BuyerUsers'],
        }),
        updateMultipleBuyerUser: builder.mutation<Entity.BuyerUser[], Entity.UpdateEntityBuyerUser[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ BUYER_USER_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['BuyerUsers'],
        }),
        deleteBuyerUser: builder.mutation<Entity.BuyerUser, Entity.BuyerUser>({
            query: (toDelete) => {
                return ({
                    url: `/${ BUYER_USER_PATH }/${Entity.PrimaryBuyerUser.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['BuyerUsers'],
        }),
        deleteMultipleBuyerUser: builder.mutation<Entity.BuyerUser[], Entity.PrimaryBuyerUser[]>({
            query: (ids) => {
                return ({
                    url: `/${ BUYER_USER_PATH }/batch${Entity.PrimaryBuyerUser.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['BuyerUsers'],
        }),
    }),
})

export const [buyerUserNormalizer, buyerUserNormalizerMiddleware] = createNormalizationMiddleware(BUYER_USER_API_REDUCER_KEY, buyerUserApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetBuyerUserListQuery,
    useGetBuyerUserMultipleQuery,
    useGetBuyerUserPresentationQuery,
    useGetBuyerUserQuery,
    useInsertBuyerUserMutation,
    useInsertMultipleBuyerUserMutation,
    useUpdateBuyerUserMutation,
    useUpdateMultipleBuyerUserMutation,
    useDeleteBuyerUserMutation,
    useDeleteMultipleBuyerUserMutation,
    
} = buyerUserApi
